import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ColombiaRail,
  ColombiaService,
  LiveRates,
  QuotePreview,
  QuotePreviewItem,
  RailConfig,
} from '../../services/colombia.service';

/**
 * Estrategia de precio a nivel PAÍS (una sola decisión, aplica a ambos rieles):
 * 'ESPEJO' = igualar a Vita automáticamente (sin perder — clamp de margen);
 * 'A'      = comisión propia (Modelo A del xls).
 */
interface PaisForm {
  estrategia: 'A' | 'ESPEJO';
  /** Piso de utilidad USD/op. En ESPEJO activa el clamp (nunca a pérdida). */
  margenMinUsd: number;
  /** Solo estrategia A. */
  comisionPct: number;
  fijoUsd: number;
  motivo: string;
}

/** Campos de contingencia, por riel (acordeón "Avanzado"). */
interface RailAvanzado {
  costoFijoCop: number;
  modoTasa: 'AUTO' | 'MANUAL';
  tasaCostoManual: number | null;
  tasaManualVigenteHasta: string | null;
}

const DEFAULT_PAIS: PaisForm = {
  // Decisión 2026-06-06: la estrategia principal es igualar a Vita sin perder.
  estrategia: 'ESPEJO',
  // 0 = "sin perder": el clamp garantiza ganancia ≥ 0 (hoy ~0.42/op con el
  // squeeze B2B). Un piso > diferencial de fees deja al cliente BAJO Vita.
  margenMinUsd: 0,
  comisionPct: 1.69,
  fijoUsd: 0.81,
  motivo: '',
};

const DEFAULT_AVANZADO: RailAvanzado = {
  // WS1.3: el fee de Vita entra vivo por API; esto es costo interno EXTRA.
  costoFijoCop: 0,
  modoTasa: 'AUTO',
  tasaCostoManual: null,
  tasaManualVigenteHasta: null,
};

/** Montos de la tabla comparativa "Vita da / damos / ganancia". */
const PREVIEW_MONTOS = [100, 500, 1000];

export interface RailColombiaConfigData {
  countryCode?: string; // hoy 'CO'
  countryName?: string;
}

/**
 * MODAL "Rail Colombia — Precio al cliente" (rediseño 2026-06-06).
 *
 * UNA decisión por país (no por riel): igualar a Vita (ESPEJO) o comisión
 * propia (A). Un solo Guardar escribe AMBOS rieles — el incidente del
 * 2026-06-06 (CO en Espejo, Bre-B olvidado en A) era la pantalla invitando
 * al error con dos cards y dos botones. La tabla comparativa en vivo
 * (quote-preview, mismo motor del backend) muestra el efecto ANTES de guardar.
 * Contingencia por riel (tasa manual, costo fijo) queda en "Avanzado".
 */
@Component({
  selector: 'app-rail-colombia-config',
  templateUrl: './rail-colombia-config.component.html',
  styleUrls: ['./rail-colombia-config.component.sass'],
})
export class RailColombiaConfigComponent implements OnInit, OnDestroy {
  readonly rails: ColombiaRail[] = ['COBREB', 'CO'];
  readonly railLabel: Record<ColombiaRail, string> = {
    COBREB: 'Bre-B (instantáneo)',
    CO: 'Transferencia (1 día)',
  };

  config: Record<ColombiaRail, RailConfig | null> = { COBREB: null, CO: null };
  live: LiveRates | null = null;

  pais: PaisForm = { ...DEFAULT_PAIS };
  avanzado: Record<ColombiaRail, RailAvanzado> = {
    COBREB: { ...DEFAULT_AVANZADO },
    CO: { ...DEFAULT_AVANZADO },
  };
  mostrarAvanzado = false;

  /** Configs vigentes con modos de pricing DISTINTOS entre rieles (estado inconsistente). */
  divergencia = false;

  preview: QuotePreview | null = null;
  previewLoading = false;
  private previewTimer: any = null;
  private previewSeq = 0;

  loading = false;
  saving = false;
  error: string | null = null;
  okMsg: string | null = null;

  /** Master de COP: si OFF, no se ofrece USD→COP (ni CLP→COP). Switchboard. */
  usdCopOfrecido = true;

  constructor(
    private svc: ColombiaService,
    public dialogRef: MatDialogRef<RailColombiaConfigComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RailColombiaConfigData,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const [config, live, railes] = await Promise.all([
        this.svc.getConfig(),
        this.svc.getLive(),
        this.svc.getRailesActivos().catch(() => null),
      ]);
      this.config = config;
      this.live = live;
      // usd_cop = master de COP (si está OFF no se ofrece USD→COP ni CLP→COP).
      this.usdCopOfrecido = railes?.cop?.usdt_cop !== false;
      this.fromConfigs(config);
      this.refreshPreview(true);
    } catch (e: any) {
      this.error = e?.message || 'No se pudo cargar la configuración';
    } finally {
      this.loading = false;
    }
  }

  /** Deriva el form país-nivel de las configs vigentes (y detecta divergencia). */
  private fromConfigs(config: Record<ColombiaRail, RailConfig | null>): void {
    const cb = config.COBREB;
    const co = config.CO;
    const modoDe = (c: RailConfig | null): 'A' | 'ESPEJO' =>
      c?.modoPricing === 'ESPEJO' ? 'ESPEJO' : 'A';

    this.divergencia = !!cb && !!co && modoDe(cb) !== modoDe(co);
    const base = cb ?? co;
    this.pais = {
      // Si divergen, proponemos ESPEJO (la estrategia principal) — al guardar
      // quedan ALINEADOS los dos rieles y la divergencia desaparece.
      estrategia: this.divergencia ? 'ESPEJO' : modoDe(base),
      margenMinUsd: Number(base?.margenMinUsd ?? DEFAULT_PAIS.margenMinUsd) || 0,
      comisionPct: Number(base?.comisionPct ?? DEFAULT_PAIS.comisionPct),
      fijoUsd: Number(base?.fijoUsd ?? DEFAULT_PAIS.fijoUsd),
      motivo: '',
    };
    for (const rail of this.rails) {
      const c = config[rail];
      this.avanzado[rail] = c
        ? {
            costoFijoCop: Number(c.costoFijoCop) || 0,
            modoTasa: c.modoTasa,
            tasaCostoManual:
              c.tasaCostoManual != null ? Number(c.tasaCostoManual) : null,
            tasaManualVigenteHasta: c.tasaManualVigenteHasta
              ? c.tasaManualVigenteHasta.slice(0, 16)
              : null,
          }
        : { ...DEFAULT_AVANZADO };
    }
  }

  /** Recalcula la tabla comparativa con la config del FORM (debounce). */
  onFormChange(): void {
    this.okMsg = null;
    this.refreshPreview();
  }

  refreshPreview(inmediato = false): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(
      () => this.fetchPreview(),
      inmediato ? 0 : 600,
    );
  }

  private async fetchPreview(): Promise<void> {
    const seq = ++this.previewSeq;
    this.previewLoading = true;
    try {
      const p = await this.svc.quotePreview({
        montos: PREVIEW_MONTOS,
        modoPricing: this.pais.estrategia,
        comisionPct: Number(this.pais.comisionPct) || 0,
        fijoUsd: Number(this.pais.fijoUsd) || 0,
        margenMinUsd: Math.max(0, Number(this.pais.margenMinUsd) || 0),
      });
      if (seq === this.previewSeq) this.preview = p;
    } catch {
      if (seq === this.previewSeq) this.preview = null;
    } finally {
      if (seq === this.previewSeq) this.previewLoading = false;
    }
  }

  /** UN guardar = AMBOS rieles (misma estrategia — no pueden divergir). */
  async saveAll(): Promise<void> {
    for (const rail of this.rails) {
      const av = this.avanzado[rail];
      if (av.modoTasa === 'MANUAL') {
        if (!(Number(av.tasaCostoManual) > 0) || !av.tasaManualVigenteHasta) {
          this.error = `MANUAL en ${this.railLabel[rail]} requiere tasa y vencimiento futuros (en Avanzado)`;
          return;
        }
      }
    }
    this.saving = true;
    this.error = null;
    this.okMsg = null;
    let guardados = 0;
    try {
      for (const rail of this.rails) {
        const av = this.avanzado[rail];
        await this.svc.upsertConfig({
          rail,
          comisionPct: Number(this.pais.comisionPct),
          fijoUsd: Number(this.pais.fijoUsd),
          costoFijoCop: Number(av.costoFijoCop) || 0,
          modoPricing: this.pais.estrategia,
          margenMinUsd: Math.max(0, Number(this.pais.margenMinUsd) || 0),
          modoTasa: av.modoTasa,
          tasaCostoManual:
            av.modoTasa === 'MANUAL' ? Number(av.tasaCostoManual) : undefined,
          tasaManualVigenteHasta:
            av.modoTasa === 'MANUAL' && av.tasaManualVigenteHasta
              ? new Date(av.tasaManualVigenteHasta).toISOString()
              : undefined,
          motivo: this.pais.motivo || undefined,
        });
        guardados++;
      }
      // Switchboard: ofrecer (o no) USD→COP al cliente. usd_cop es el master de
      // COP — si se apaga, el backend también deja de ofrecer CLP→COP.
      await this.svc.setRailesActivos({ usdtCop: this.usdCopOfrecido });
      this.okMsg = `Guardado en ambos rieles (${this.pais.estrategia === 'ESPEJO' ? 'Igualar a Vita' : 'Comisión propia'}).`;
      await this.load();
    } catch (e: any) {
      // Si falló el SEGUNDO upsert, el primero ya quedó: estado divergente.
      // El banner de divergencia lo va a mostrar al recargar — reintentar
      // Guardar lo resuelve (re-escribe ambos).
      const detalle = e?.error?.message || e?.message || 'No se pudo guardar';
      this.error =
        guardados > 0 && guardados < this.rails.length
          ? `${detalle} — se guardó solo ${guardados} de ${this.rails.length} rieles: REINTENTÁ Guardar para alinearlos.`
          : detalle;
      await this.load();
    } finally {
      this.saving = false;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  // ── Helpers de vista ──

  itemsDe(rail: ColombiaRail) {
    return this.preview?.rails?.[rail]?.items ?? [];
  }

  /** Diferencia damos − Vita (COP). Negativo = el cliente recibe menos que en Vita. */
  difVsVita(item: QuotePreviewItem): number | null {
    const vita = item?.ofertaVitaCop;
    const damos = item?.quote?.resultado?.recibeCop;
    if (vita == null || damos == null) return null;
    return damos - vita;
  }

  // Accessors null-safe para el template (strict template checking).
  recibeDe(item: QuotePreviewItem): number | null {
    return item?.quote?.resultado?.recibeCop ?? null;
  }
  gananciaDe(item: QuotePreviewItem): number | null {
    return item?.quote?.resultado?.gananciaUsd ?? null;
  }
  clampedDe(item: QuotePreviewItem): boolean {
    return !!item?.quote?.resultado?.clamped;
  }
  margenOkDe(item: QuotePreviewItem): boolean {
    return !!item?.quote?.margenOk;
  }

  /** true si algún monto del preview quedó clampeado (damos < Vita por el piso). */
  get hayClamp(): boolean {
    return this.rails.some((r) =>
      this.itemsDe(r).some((i: any) => i?.quote?.resultado?.clamped),
    );
  }

  /** true si el ESPEJO está degradado (sin oferta retail viva). */
  get espejoDegradado(): boolean {
    return this.rails.some((r) =>
      this.itemsDe(r).some((i: any) => i?.quote?.espejoFallback),
    );
  }

  partnerDe(rail: ColombiaRail): number | null {
    return this.live?.rails?.[rail]?.partnerUsdSell ?? null;
  }
  publicaDe(rail: ColombiaRail): number | null {
    return this.live?.rails?.[rail]?.publicUsdSell ?? null;
  }
  feeDe(rail: ColombiaRail): number | null {
    return this.live?.rails?.[rail]?.fixedCostCop ?? null;
  }
  diasDe(rail: ColombiaRail): number | null {
    return this.live?.rails?.[rail]?.businessDays ?? null;
  }
  esVigente(rail: ColombiaRail): boolean {
    return !!this.config[rail];
  }

  /** WS1.3: costo fijo > 0 probablemente duplica el fee de Vita (ya entra vivo). */
  costoFijoDuplicaFee(rail: ColombiaRail): boolean {
    return Number(this.avanzado[rail]?.costoFijoCop) > 0;
  }
}
